import { Platform, SmartSearchAttribute } from 'componentregistry';
import {
  Filters,
  NestedFilter,
  NumberFilter,
  StringFilter,
  NonNestedFilter,
  MultipleNumberFilter,
  MultipleStringFilter,
  DATE_OPS_KEY_TYPE,
  FilterEntry,
  SingleFilter,
  BooleanFilter,
  isNestedFilter,
  SmartSearchGridData,
  isOpCaseInsensitive,
  STRING_OPS_KEY_TYPE,
  NormalizedAttributeType,
  Attribute,
  getFilterDataType,
  OPS_KEY_TYPE,
} from '@cloudio-saas/datasource-types';
import { ulid } from 'ulid';
import {
  allBooleanOps,
  allDateOps,
  allNumberOps,
  allSelectOps,
  allStringOps,
  allYNTFOps,
  getOperatorsArray,
  hasEditor,
  Operator,
} from 'components/SmartSearch/Operators';
import {
  ClientFilter,
  ClientStringFilter,
  ClientNestedFilter,
  nestedOptions,
  ClientNonNestedFilter,
  ClientDateFilter,
  ClientNumberFilter,
  ClientMultipleDateFilter,
  ClientMultipleStringFilter,
  ClientBooleanFilter,
  DateValueType,
  MultipleDateValueType,
  ClientMultipleSelectFilter,
  ClientSelectFilter,
  ClientTFFilter,
  ClientYNFilter,
} from 'components/SmartSearch/types';
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  addDays,
  addMinutes,
} from 'date-fns';
import { isNotEmpty, isNull } from 'utils/isEmpty';
import stringify from 'json-stable-stringify';
import { AttributeDataType } from 'componentregistry/components/SmartSearch';
import { TFValues, YNValues } from 'componentregistry/registry/constants';
import { TranslateFn } from '../../utils/lang';
import { userTimeZoneAtom } from 'utils/state';
import { getTimezoneOffset } from 'date-fns-tz';
import { userAtom } from 'root/useUser';
import { getFilterBaseDataType } from 'componentregistry/actions/ActionSetQueryFilter/ActionSetQueryFilterManager';

export type Combiner = 'allOf' | 'anyOf' | 'noneOf';

export const newEntry = (): ClientStringFilter => {
  return {
    key: ulid(),
    name: '',
    dataType: 'Text',
    label: '',
    operator: 'is',
    editing: true,
    value: '',
    excludeOperators: '',
  };
};

export const newFilterEntry = (): FilterEntry<unknown> => {
  return {
    '': { is: '' },
  };
};

export const isNested = (name: string): boolean => {
  return ['allOf', 'anyOf', 'noneOf'].includes(name);
};

export const getName = (entry: FilterEntry<unknown>): string => {
  return Object.keys(entry)[0];
};

export const getOperator = (entry: SingleFilter<unknown>): OPS_KEY_TYPE => {
  const name = getName(entry) as keyof unknown;
  const op = entry[name] as NonNestedFilter;
  return Object.keys(op)[0] as OPS_KEY_TYPE;
};

const adjustDateToUserTimezone = (
  value: Date | null,
  platform?: Platform | null,
) => {
  if (!value) {
    return null;
  }
  if (!platform) {
    (value as Date).toISOString();
  }
  const dateValue = new Date(value);
  if (platform) {
    const user = platform.getter(userAtom);
    const userTimeZone =
      user &&
      platform.getter(userTimeZoneAtom, {
        params: { userName: user.userName },
      });
    if (userTimeZone) {
      const userTzOffset = getTimezoneOffset(userTimeZone) / (60 * 1000);
      const currentTzOffset = new Date().getTimezoneOffset();
      const tzDiff = userTzOffset - -1 * currentTzOffset;
      return addMinutes(dateValue, -1 * tzDiff).toISOString();
    }
  }
  return dateValue.toISOString();
};

export const getISOStringDate = (
  date: Date | null,
  platform?: Platform | null,
): string | null => {
  return date ? adjustDateToUserTimezone(date, platform) : null;
};

export const getISOStringDateRange = (
  dates: (Date | null)[],
  platform?: Platform | null,
) => {
  return dates.map((date) => getISOStringDate(date, platform));
};

export const convertISOToDate = (
  d: DateValueType,
  dataType: string | undefined | null,
  showTime: boolean | undefined,
  platform: Platform,
): Date | null => {
  if (!d) {
    return null;
  }
  if (typeof d === 'string') {
    try {
      let dVal = parseISO(d);
      if (String(dVal) === 'Invalid Date') {
        return null;
      }
      const tzOffset = dVal.getTimezoneOffset();
      if (dataType && (dataType === 'Date' || dataType === 'StringDateTime')) {
        dVal = addMinutes(dVal, tzOffset);
      } else if (dataType === 'DateTime' && showTime) {
        const user = platform.getter(userAtom);
        const userTimeZone =
          user &&
          platform.getter(userTimeZoneAtom, {
            params: { userName: user.userName },
          });
        if (userTimeZone) {
          const userTzOffset = getTimezoneOffset(userTimeZone) / (60 * 1000);
          const currentTzOffset = new Date().getTimezoneOffset();
          const tzDiff = userTzOffset - -1 * currentTzOffset;
          dVal = addMinutes(dVal, tzDiff);
        }
      }

      return dVal;
    } catch (e) {
      return null;
    }
  }
  try {
    const dStr = d.toISOString();
    return convertISOToDate(dStr, dataType, showTime, platform);
  } catch (e) {
    return null;
  }
};

export const convertToDate = (d: DateValueType): Date | null => {
  if (!d) {
    return null;
  }
  if (typeof d === 'string') {
    try {
      const dVal = parseISO(d);
      if (String(dVal) === 'Invalid Date') {
        return null;
      }
      return dVal;
    } catch (e) {
      return null;
    }
  }
  try {
    const dStr = d.toISOString();
    return convertToDate(dStr);
  } catch (e) {
    return null;
  }
};

export const convertToDateOrDateRange = (
  val: DateValueType | MultipleDateValueType,
  dataType: string | undefined | null,
  showTime: boolean | undefined,
  platform: Platform,
): (Date | null) | (Date | null)[] => {
  if (Array.isArray(val)) {
    return val.map((v) => convertISOToDate(v, dataType, showTime, platform));
  }
  return convertISOToDate(val, dataType, showTime, platform);
};

export const getNestedFilters = (
  entry: NestedFilter<unknown>,
): Filters<unknown> => {
  const name = getName(entry) as Combiner;
  const filters = entry[name] as Filters<unknown>;
  return filters;
};

export const getValue = (entry: SingleFilter<unknown>): unknown => {
  const name = getName(entry);
  const op = entry[name as keyof unknown] as NonNestedFilter;
  const firstKey = Object.keys(op)[0];
  if (firstKey) {
    return op[firstKey as keyof typeof op];
  }
  return undefined;
};

export const isComplete = (entry: ClientFilter): boolean => {
  if (!entry) {
    return false;
  }
  if (isNested(entry.name)) {
    return isNestedComplete(entry);
  }
  entry = entry as ClientNonNestedFilter;
  return !!(
    entry.name &&
    entry.operator &&
    ((entry.dataType && !hasEditor(entry)) || isNotEmpty(entry.value))
  );
};

export const isFilterEntryComplete = (entry: FilterEntry<unknown>): boolean => {
  if (!entry) {
    return false;
  }
  const name = getName(entry);
  if (isNested(name)) {
    return isNestedEntryComplete(entry);
  }
  const operator = getOperator(entry);
  const value = getValue(entry);

  return !!(
    name &&
    operator &&
    (isFixedOp(operator as DATE_OPS_KEY_TYPE) || !isNull(value))
  );
};

export const isNestedEntryComplete = (entry: NestedFilter<unknown>) => {
  const nestedFilters = getNestedFilters(entry) ?? [];
  const completeFilters: FilterEntry<unknown>[] = nestedFilters.filter((ent) =>
    isFilterEntryComplete(ent),
  );
  return completeFilters.length > 0;
};

export const isNestedComplete = (entry: ClientFilter): boolean => {
  const completeEntries: FilterEntry<unknown>[] =
    (entry as ClientNestedFilter).filters?.filter((ent) =>
      isFilterEntryComplete(ent),
    ) ?? [];
  return completeEntries.length > 0;
};

const numberToSelectOps: Record<string, string> = {
  is: 'eq',
  not: 'ne',
  empty: 'null',
  notEmpty: 'notNull',
};
const selectToNumberOps: Record<string, string> = {
  eq: 'is',
  ne: 'not',
  null: 'empty',
  notNull: 'notEmpty',
};

export const toNumberOp = (op: string) => {
  return numberToSelectOps[op] || op;
};

export const toSelectOp = (op: string): OPS_KEY_TYPE => {
  return (selectToNumberOps[op] || op) as OPS_KEY_TYPE;
};

const toNumber = (v: unknown): { value: number; isValid: boolean } => {
  if (typeof v === 'number') {
    return { value: v, isValid: true };
  }
  if (typeof v === 'string') {
    const retv = Number(v);
    if (Number.isNaN(retv)) {
      return { value: retv, isValid: false };
    }
    return { value: retv, isValid: true };
  }
  return { value: -1, isValid: false };
};

export const toNumberValue = (
  value: unknown,
): { value: number | number[]; isValid: boolean } => {
  if (!value) {
    return { value: 0, isValid: true };
  }
  if (Array.isArray(value)) {
    const retArr: number[] = [];
    let isValid = true;
    value.forEach((v) => {
      const retV = toNumber(v);
      if (retV.isValid) {
        retArr.push(retV.value);
      } else {
        isValid = false;
      }
    });
    return { value: retArr, isValid };
  }
  return toNumber(value);
};

export const toStringValue = (value: unknown) => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v !== 'string' ? String(v) : v));
  }
  return String(value);
};

const isYN = (v: unknown) => {
  const { checkedValue, unCheckedValue } = YNValues;
  return v === checkedValue || v === unCheckedValue;
};
const toYN = (v: unknown) => {
  const { checkedValue, unCheckedValue } = YNValues;
  if (typeof v === 'string') {
    return v.toUpperCase().startsWith(checkedValue)
      ? checkedValue
      : unCheckedValue;
  }
  if (typeof v === 'number') {
    return v <= 0 ? unCheckedValue : checkedValue;
  }
  return v ? checkedValue : unCheckedValue;
};
export const toYNValue = (value: unknown) => {
  if (!value) return '';

  if (Array.isArray(value)) {
    return value.map((v) => (!isYN(v) ? toYN(v) : v));
  }
  return toYN(value);
};
const isTF = (v: unknown) => {
  const { checkedValue, unCheckedValue } = TFValues;
  return v === checkedValue || v === unCheckedValue;
};
const toTF = (v: unknown) => {
  const { checkedValue, unCheckedValue } = TFValues;
  if (typeof v === 'string') {
    return v.toUpperCase().startsWith(checkedValue)
      ? checkedValue
      : unCheckedValue;
  }
  if (typeof v === 'number') {
    return v <= 0 ? unCheckedValue : checkedValue;
  }
  return v ? checkedValue : unCheckedValue;
};
export const toTFValue = (value: unknown) => {
  if (!value) return '';

  if (Array.isArray(value)) {
    return value.map((v) => (!isTF(v) ? toYN(v) : v));
  }
  return toTF(value);
};

export const toBooleanValue = (value: unknown) => {
  if (!value) return false;

  if (Array.isArray(value)) {
    return value.map((v) => Boolean(v));
  }
  return Boolean(value);
};

const adjustDateToTimezone = (value: Date | null) => {
  if (!value) {
    return null;
  }
  const dateValue = new Date(value);
  const userTzOffset = dateValue.getTimezoneOffset();
  return addMinutes(dateValue, -1 * userTzOffset).toISOString();
};

const adjustDateRangeToTimezone = (dates: (Date | null)[]) => {
  if (!dates) {
    return null;
  }
  return dates.map((date) => adjustDateToTimezone(date));
};

export const toValidDateString = (
  value: unknown,
  baseDataType: string | null,
  fromSetQueryFilter?: boolean,
): { value: string; isValid: boolean } => {
  const d = convertToDate((value ?? new Date()) as Date | string | null);
  if (!d || d === null || Number.isNaN(d)) {
    return { value: '', isValid: false };
  }
  if (
    fromSetQueryFilter &&
    (baseDataType === 'Date' || baseDataType === 'StringDateTime')
  ) {
    return { value: adjustDateToTimezone(d) ?? d.toISOString(), isValid: true };
  }
  return { value: d.toISOString(), isValid: true };
};
export const toValidDateValue = (
  value: unknown,
  baseDataType: string | null,
  fromSetQueryFilter?: boolean,
) => {
  if (!value) return { value: '', isValid: true };
  if (Array.isArray(value)) {
    const retArr: string[] = [];
    let isValid = true;
    value.forEach((v) => {
      const retV = toValidDateString(v, baseDataType, fromSetQueryFilter);
      if (retV.isValid) {
        retArr.push(retV.value);
      } else {
        isValid = false;
      }
    });
    return { value: retArr, isValid };
  }
  return toValidDateString(value, baseDataType, fromSetQueryFilter);
};

export const recordEntry = (
  platform: Platform,
  itemId: string,
  entry: ClientFilter,
): void => {
  if (!platform.isRecording()) {
    return;
  }

  if (isNested(entry.name)) {
    // TODO: record nested
    return;
  }
  entry = entry as ClientNonNestedFilter;
  const { value, name, label } = entry;
  let { operator } = entry;
  let op = '';
  let val = '';

  if (name && operator) {
    if (entry.dataType === 'Date') {
      operator = operator as keyof typeof allDateOps;
      op = allDateOps[operator];
      val = operator === 'bn' ? '[new Date(), new Date()]' : 'new Date()';
    } else if (entry.dataType === 'Number') {
      operator = operator as keyof typeof allNumberOps;
      op = allNumberOps[operator];
      entry = entry as ClientNumberFilter | ClientMultipleDateFilter;
      const numValue = toNumberValue(value);
      val = JSON.stringify(numValue.value);
    } else if (entry.dataType === 'Boolean') {
      operator = operator as keyof typeof allBooleanOps;
      op = allBooleanOps[operator];
      entry = entry as ClientBooleanFilter;
      val = JSON.stringify(value ?? false);
    } else if (
      (entry.dataType === 'Select' || entry.dataType === 'Lookup') &&
      entry.baseDataType === 'Number'
    ) {
      operator = operator as keyof typeof allSelectOps;
      op = allSelectOps[operator];
      entry = entry as ClientSelectFilter | ClientMultipleSelectFilter;
      const numValue = toNumberValue(value);
      val = JSON.stringify(numValue.value);
    } else if (
      (entry.dataType === 'Select' || entry.dataType === 'Lookup') &&
      entry.baseDataType !== 'Number'
    ) {
      operator = operator as keyof typeof allSelectOps;
      op = allSelectOps[operator];
      entry = entry as ClientSelectFilter | ClientMultipleSelectFilter;
      val = JSON.stringify(value);
    } else if (entry.dataType === 'YN') {
      operator = operator as keyof typeof allYNTFOps;
      op = allYNTFOps[operator];
      entry = entry as ClientYNFilter | ClientTFFilter;
      val = value === 'Y' ? "'Checked'" : "'Unchecked'";
    } else if (entry.dataType === 'TF') {
      operator = operator as keyof typeof allYNTFOps;
      op = allYNTFOps[operator];
      entry = entry as ClientYNFilter | ClientTFFilter;
      val = value ? "'True'" : "'False'";
    } else {
      operator = operator as keyof typeof allStringOps;
      op = allStringOps[operator];
      entry = entry as ClientStringFilter | ClientMultipleStringFilter;
      val = JSON.stringify(value);
    }

    platform.recordLine(
      itemId,
      `await ui.typeSmartSearchFilter('${itemId}', '${label}', '${op}', ${val});`,
    );
  }
};

export const toFilterEntry = (
  platform: Platform,
  entry: ClientFilter,
): FilterEntry<unknown> => {
  if (isNested(entry.name)) {
    const filter: NestedFilter<unknown> = {
      [entry.name as Combiner]: (entry as ClientNestedFilter).filters,
    };
    return filter;
  }
  entry = entry as ClientNonNestedFilter;
  if (entry.name && entry.operator) {
    if (entry.dataType === 'Date') {
      entry = entry as ClientDateFilter | ClientMultipleDateFilter;
      if (
        entry.baseDataType === 'StringDateTime' ||
        entry.baseDataType === 'Date'
      ) {
        const dateFilterValue =
          entry.operator === 'bn'
            ? adjustDateRangeToTimezone(entry.value as (Date | null)[])
            : adjustDateToTimezone(entry.value as Date | null);
        const filter = {
          [entry.name]: { [entry.operator as string]: dateFilterValue },
          includesTime: entry.includesTime ? 'Y' : 'N',
        };

        return filter;
      } else {
        const convert = entry.includesTime ? platform : null;
        const dateFilterValue =
          entry.operator === 'bn'
            ? getISOStringDateRange(entry.value as (Date | null)[], convert)
            : getISOStringDate(entry.value as Date | null, convert);
        const filter = {
          [entry.name]: { [entry.operator as string]: dateFilterValue },
          includesTime: entry.includesTime ? 'Y' : 'N',
        };

        return filter;
      }
    }
    if (entry.dataType === 'Number') {
      entry = entry as ClientNumberFilter | ClientMultipleDateFilter;
      const numValue = toNumberValue(entry.value);
      const filter: NumberFilter | MultipleNumberFilter = {
        [entry.name]: { [entry.operator as string]: numValue.value },
      };
      return filter;
    }

    if (entry.dataType === 'Boolean') {
      entry = entry as ClientBooleanFilter;
      const filter: BooleanFilter = {
        [entry.name]: { [entry.operator as string]: entry.value ?? false },
      };
      return filter;
    }

    if (
      (entry.dataType === 'Select' || entry.dataType === 'Lookup') &&
      entry.baseDataType === 'Number'
    ) {
      entry = entry as ClientSelectFilter | ClientMultipleSelectFilter;
      const numValue = toNumberValue(entry.value);
      const filter: NumberFilter | MultipleNumberFilter = {
        [entry.name]: {
          [toNumberOp(entry.operator as string)]: numValue.value,
        },
      };
      return filter;
    }

    entry = entry as ClientStringFilter | ClientMultipleStringFilter;
    const filter: StringFilter | MultipleStringFilter = {
      [entry.name]: { [entry.operator as string]: entry.value ?? '' },
    };
    return filter;
  }
  return {};
};

export const toFilter = (
  platform: Platform,
  entries: ClientFilter[],
): Filters<unknown> => {
  if (!entries || entries.length === 0) {
    return [];
  }
  const completeEntries = entries.filter((entry) => isComplete(entry));
  if (completeEntries.length === 0) {
    return [];
  }
  return completeEntries.map((entry) => toFilterEntry(platform, entry));
};

export const toClientFilterEntry = (
  entry: FilterEntry<unknown>,
  items: SmartSearchAttribute.SmartSearchAttributeItem[],
  platform: Platform,
): ClientFilter => {
  const name = getName(entry);
  if (isNested(name)) {
    entry = entry as NestedFilter<unknown>;
    const nestedOption = nestedOptions.find((option) => option.value === name);
    const retFilter: ClientNestedFilter = {
      key: ulid(),
      name: name as Combiner,
      label: nestedOption?.label ?? name,
      filters: (entry as NestedFilter<unknown>)[name as Combiner] ?? [],
      editing: false,
    };
    return retFilter;
  }

  const item = items.find((it) => it.props.attributeCode === name);
  let operator = getOperator(entry as NonNestedFilter);
  let value: unknown = getValue(entry as NonNestedFilter);
  if (item?.props.dataType === 'Date') {
    value = convertToDateOrDateRange(
      value as DateValueType | MultipleDateValueType,
      item?.props.baseDataType,
      item?.props.showTime,
      platform,
    );
  }
  if (
    (item?.props.dataType === 'Select' || item?.props.dataType === 'Lookup') &&
    item?.props.baseDataType === 'Number'
  ) {
    operator = toSelectOp(operator);
    value = toStringValue(value);
  }
  return {
    key: ulid(),
    label: item?.props.label ?? '',
    name: getName(entry),
    operator,
    editing: false,
    dataType: item?.props.dataType,
    baseDataType: item?.props.baseDataType,
    includesTime: item?.props.showTime,
    value,
  } as ClientNonNestedFilter;
};

/*
export const toClientFilter = (
  entries: Filters<unknown>,
  items: SmartSearchAttribute.SmartSearchAttributeItem[],
): ClientFilter[] => {
  if (entries.length === 0) {
    return [];
  }
  return entries.map((entry) => toClientFilterEntry(entry, items));
};
*/
export const isRange = (key: DATE_OPS_KEY_TYPE) => {
  return [
    'thisWeek',
    'thisMonth',
    'thisQuarter',
    'thisYear',
    'last7days',
    'last14days',
    'last28days',
    'bn',
  ].includes(key);
};

export const isFixedOp = (key: DATE_OPS_KEY_TYPE): boolean => {
  return [
    'today',
    'tomorrow',
    'yesterday',
    'thisWeek',
    'thisMonth',
    'thisQuarter',
    'thisYear',
    'last7days',
    'last14days',
    'last28days',
    'empty',
    'notEmpty',
  ].includes(key);
};

export const isFixedOperator = (op: string): boolean => {
  return [
    'today',
    'tomorrow',
    'yesterday',
    'thisWeek',
    'thisMonth',
    'thisQuarter',
    'thisYear',
    'last7days',
    'last14days',
    'last28days',
    'empty',
    'notEmpty',
    'null',
    'notNull',
  ].includes(op);
};

export const isMultiOperator = (op: string) => {
  return [
    'in',
    'iIn',
    'nin',
    'iNin',
    'hasAll',
    'iHasAll',
    'hasAny',
    'iHasAny',
    'notAny',
    'iNotAny',
  ].includes(op);
};

export const getFixedDate = (op: DATE_OPS_KEY_TYPE): Date | null => {
  const date = new Date();
  if (op === 'today') {
    return date;
  }
  if (op === 'yesterday') {
    return subDays(date, 1);
  }
  if (op === 'tomorrow') {
    return addDays(date, 1);
  }
  return null;
};

export const getFixedRange = (
  op: DATE_OPS_KEY_TYPE,
): [Date | null, Date | null] => {
  const date = new Date();
  if (op === 'last7days') {
    return [subDays(date, 7), date];
  }
  if (op === 'last14days') {
    return [subDays(date, 14), date];
  }
  if (op === 'last28days') {
    return [subDays(date, 28), date];
  }
  if (op === 'thisWeek') {
    return [startOfWeek(date), endOfWeek(date)];
  }
  if (op === 'thisMonth') {
    return [startOfMonth(date), endOfMonth(date)];
  }
  if (op === 'thisQuarter') {
    return [startOfQuarter(date), endOfQuarter(date)];
  }
  if (op === 'thisYear') {
    return [startOfYear(date), endOfYear(date)];
  }
  return [null, null];
};

export const areFiltersEqual = (
  filters1: Filters<unknown>,
  filters2: Filters<unknown>,
): boolean => {
  return stringify(filters1) === stringify(filters2);
};

export const areGridDatasEqualExcludingChildren = (
  gridData1: SmartSearchGridData,
  gridData2: SmartSearchGridData,
): boolean => {
  return (
    areFiltersEqual(gridData1.gridFilter ?? [], gridData2.gridFilter ?? []) &&
    stringify(gridData1.gridSort ?? {}) ===
      stringify(gridData2.gridSort ?? {}) &&
    stringify(gridData1.runtimeProps ?? {}) ===
      stringify(gridData2.runtimeProps ?? {})
  );
};

export const areGridDatasEqual = (
  gridData1: SmartSearchGridData,
  gridData2: SmartSearchGridData,
): boolean => {
  return (
    areGridDatasEqualExcludingChildren(gridData1, gridData2) &&
    stringify(gridData1.runtimeChildren ?? []) ===
      stringify(gridData2.runtimeChildren ?? [])
  );
};

export const makeFilterEntryValid = (
  f: FilterEntry<unknown>,
  getAttribute: (attr: string) => Attribute | undefined,
  fromSetQueryFilter?: boolean,
) => {
  const name = getName(f);
  const attribute = getAttribute(name);
  const filterDataType = attribute ? getFilterDataType(attribute) : undefined;
  const baseDataType = attribute ? getFilterBaseDataType(attribute) : undefined;
  const operator = getOperator(f);
  if (!filterDataType) {
    return undefined;
  }
  let value = getValue(f);
  if (filterDataType === 'Number') {
    const validValue = toNumberValue(value);
    return validValue.isValid
      ? { [name]: { [operator]: validValue.value } }
      : undefined;
  }
  if (filterDataType === 'String') {
    value = toStringValue(value);
    return { [name]: { [operator]: value } };
  }
  if (filterDataType === 'YN') {
    value = toYNValue(value);
    return { [name]: { [operator]: value } };
  }
  if (filterDataType === 'TF') {
    value = toTFValue(value);
    return { [name]: { [operator]: value } };
  }
  if (filterDataType === 'Boolean') {
    value = toBooleanValue(value);
    return { [name]: { [operator]: value } };
  }
  if (filterDataType === 'Date') {
    //@ts-ignore todo! change the date filter entry type
    const includesTime = f['includesTime'];
    const validValue = toValidDateValue(
      value,
      baseDataType ?? filterDataType,
      fromSetQueryFilter,
    );
    // const includesTime = 'includesTime';
    return validValue.isValid
      ? { [name]: { [operator]: validValue.value }, includesTime: includesTime }
      : undefined;
  }
  return f;
};

export const makeValid = (
  filters: Filters<unknown>,
  getAttribute: (attr: string) => Attribute | undefined,
  context?: { itemId: string },
) => {
  const validFilters: Filters<unknown> = [];
  filters.forEach((f) => {
    if (isFilterEntryComplete(f)) {
      // incomplete filter ignored
    }
    if (isNestedFilter(f)) {
      const validNestedFilter = makeValid(getNestedFilters(f), getAttribute);
      if (validNestedFilter.length > 0) {
        validFilters.push({
          [getName(f)]: validNestedFilter,
        });
      }
    } else {
      const validFilter = makeFilterEntryValid(f, getAttribute);
      if (!validFilter) {
        // invalid filter found
      } else {
        validFilters.push(validFilter);
      }
    }
  });
  return validFilters;
};

export const mapFilters = (
  filters: Filters<unknown>,
  mappings: { copyFromAttribute: string; copyToAttribute: string }[],
): Filters<unknown> => {
  const mappedFilters: Filters<unknown> = [];
  filters.forEach((f) => {
    const name = getName(f);
    if (isNested(name)) {
      const nestedFilters = mapFilters(
        (f as NestedFilter<unknown>)[name as Combiner] as Filters<unknown>,
        mappings,
      );
      if (nestedFilters.length) {
        mappedFilters.push({ [name]: nestedFilters });
      }
    }
    const mapping = mappings.find((m) => m.copyFromAttribute === name);
    if (mapping) {
      mappedFilters.push({
        [mapping.copyToAttribute]: { [getOperator(f)]: getValue(f) },
      });
    }
  });
  return mappedFilters;
};

export const getFilterAttributeDataType = (
  type: NormalizedAttributeType,
): AttributeDataType => {
  return type === 'String' ? 'Text' : type;
};

export const getOpOptions = (
  dataType: AttributeDataType,
  t: TranslateFn,
  caseIns?: boolean,
  excludeOperators?: string,
): { value: OPS_KEY_TYPE; label: string }[] => {
  let opArray = getOperatorsArray(dataType) ?? [];
  if (dataType === 'Text') {
    opArray = opArray.filter((op: Operator) => {
      const isCaseInsensitive = isOpCaseInsensitive(
        op.value as STRING_OPS_KEY_TYPE,
      );
      const emptyOp = ['empty', 'notEmpty'].includes(op.value);
      return emptyOp || (caseIns ? isCaseInsensitive : !isCaseInsensitive);
    });
  }
  if (excludeOperators) {
    const excluded = new Set(excludeOperators.split(';'));
    opArray = opArray.filter((op: Operator) => !excluded.has(op.value));
  }
  return opArray.map((op: Operator) => ({
    value: op.value,
    label: t(op.label),
  }));
};
